import {Component,OnInit} from "@angular/core";
import {ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {ListItem} from "@domain/common/list-view/list-item";
import {TranslateService} from "@ngx-translate/core";
import {Sorting} from "@domain/common/list-view/sorting";
import {IndemnitesAbattementsListItemComponent} from "@components/admin/frais/indemnites/indemnites-abattements/indemnites-abattements-list-item/indemnites-abattements-list-item.component";
import {HttpClient,HttpParams} from "@angular/common/http";
import {environment} from "@environments/environment";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {ToastrService} from "ngx-toastr";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {filter,finalize} from "rxjs/operators";
import {BehaviorSubject} from "rxjs";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {Router} from "@angular/router";

/**
 * Onglet abattements
 *
 * @author Guillaume TRAVOT
 * @date 26/04/2024
 */
@Component({
    host: {'[attr.data-test-id]': "'indemnites-abattements'"},
    templateUrl: './indemnites-abattements.component.html'
})
export class IndemnitesAbattementsComponent implements OnInit {
    /** Liste des abattements en affichage V10 */
    listeAbattements: ListView<IndemnitesAbattementItem,IndemnitesAbattementsListItemComponent>;

    /** Liste des actions du bouton flottant */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /**
     * Constructeur
     */
    constructor(private translateService: TranslateService,
                private http: HttpClient,
                private toastrService: ToastrService,
                private confirmService: ConfirmService,
                private router: Router) {
    }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Initialisation du bouton flottant de création
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-ajouter',
            libelle: 'global.actions.creer',
            doAction: () => this.createAbattement()
        }]);

        //Initialisation de la liste V10 en mode backend standard
        this.listeAbattements = new ListView<IndemnitesAbattementItem,IndemnitesAbattementsListItemComponent>({
            uri: '/controller/IJ/listeAbattementsSearch',
            title: this.translateService.instant('admin.frais.indemnites.onglets.abattements'),
            component: IndemnitesAbattementsListItemComponent,
            defaultOrder: 'libelleHoraire',
            isFilter: true,
            isSearchBar: true,
            isFilterVisible: true,
            mapResult: (result: Result) => this.mapListeColonnesToPage(result),
            listeFilters: [{
                clef: 'idHoraire',
                title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.regleAttribution'),
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                loading: true,
                listeValues: [],
                multiple: true,
                isDefault: true,
                clefSearchbar: 'libelleHoraire'
            }, {
                clef: 'idTypeEntite',
                title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.typeEntite'),
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                loading: true,
                listeValues: [],
                multiple: true
            }, {
                clef: 'nbJours',
                title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.nbJours'),
                type: TypeFilter[TypeFilter.DECIMAL],
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                isDefault: true
            }, {
                clef: 'pourcentageAbattement',
                title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.abattement'),
                type: TypeFilter[TypeFilter.DECIMAL],
                typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                isDefault: true
            }],
            extraOptions: {
                deleteAbattement: (abattement: IndemnitesAbattementItem) => this.deleteAbattement(abattement),
                consultAbattement: (abattement: IndemnitesAbattementItem) => this.consultAbattement(abattement)
            }
        });

        //Nom de classe pour la persistance des filtres
        this.listeAbattements.className = 'IndemnitesAbattementsV10Component';

        //Activation de la persistance des filtres
        this.listeAbattements.isPersistFilters = true;

        //Colonnes triables
        this.listeAbattements.columns = [
            {key: 'libelleHoraire',title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.regleAttribution')},
            {key: 'libelleTypeEntite',title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.typeEntite')},
            {key: 'nbJours',title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.nbJours')},
            {key: 'pourcentageAbattement',title: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.abattement')}
        ];

        //Tri par défaut
        this.listeAbattements.sorting = new Sorting(this.listeAbattements.columns,'libelleHoraire');

        this.fetchFilterValues();
    }

    /**
     * Mapping de la liste des abattements depuis la réponse backend
     */
    private mapListeColonnesToPage(result: Result): any {
        const sourceListe = result?.data?.listeAbattements ?? result;

        //Recherche du tableau de résultats pour les 2 formats :
        //- SearchResult V10 : listeResultats
        //- ListeColonnes legacy : data
        const donneesBrutes = sourceListe?.listeResultats ?? sourceListe?.data ?? [];
        const listeResultats = (donneesBrutes ?? []).map((item: Partial<IndemnitesAbattementItem>) => {
            const abattement = new IndemnitesAbattementItem(item);

            //Fallback d'affichage comme en V9 : id 0/null => "Tous"
            if (!(abattement.idHoraire > 0)) {
                abattement.libelleHoraire = this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.tous');
            }
            if (!(abattement.idTypeEntite > 0)) {
                abattement.libelleTypeEntite = this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.tous');
            }

            return abattement;
        });

        // Alimentation des filtres depuis les référentiels retournés par la recherche principale
        const filterHoraire = this.listeAbattements?.listeFilters?.find(f => f.clef === 'idHoraire');
        const filterTypeEntite = this.listeAbattements?.listeFilters?.find(f => f.clef === 'idTypeEntite');

        const listeHoraire = result?.data?.listeHoraire ?? [];
        const listeTypeEntite = result?.data?.listeTypeEntite ?? [];

        if (filterHoraire && listeHoraire.length > 0) {
            filterHoraire.listeValues = this.formatFilterOptions(listeHoraire, 'idHoraire');
            filterHoraire.loading = false;
        }

        if (filterTypeEntite && listeTypeEntite.length > 0) {
            filterTypeEntite.listeValues = this.formatFilterOptions(listeTypeEntite, 'idTypeEntite');
            filterTypeEntite.loading = false;
        }

        return {
            listeResultats,
            //SearchResult est en 0-based, ListeColonnes est en 1-based
            numPage: sourceListe?.nbPagesTotal !== undefined ? (sourceListe?.numPage ?? 0) : Math.max((sourceListe?.numPage ?? 1) - 1,0),
            nbPagesTotal: sourceListe?.nbPagesTotal ?? sourceListe?.nbPages ?? 1,
            nbObjetsTotal: sourceListe?.nbObjetsTotal ?? sourceListe?.nbObjets ?? listeResultats.length,
            nbObjetsParPage: sourceListe?.nbObjetsParPage ?? sourceListe?.objetParPage ?? 25,
            nbObjetsDansPage: listeResultats.length
        };
    }

    /**
     * Suppression d'un abattement
     */
    private deleteAbattement(abattement: IndemnitesAbattementItem): void {
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter((isConfirmed: boolean) => isConfirmed))
            .subscribe(() => {
                this.http.post<Result>(`${environment.baseUrl}/controller/IJ/deleteAbattement`,abattement).subscribe(result => {
                    if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                        this.toastrService.success(this.translateService.instant('global.success.suppression'));
                        this.listeAbattements?.refresh();
                    } else {
                        TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                    }
                });
            });
    }

    private fetchFilterValues(): void {
        const filterHoraire = this.listeAbattements.listeFilters.find(f => f.clef === 'idHoraire');
        const filterTypeEntite = this.listeAbattements.listeFilters.find(f => f.clef === 'idTypeEntite');

        // Si les données sont déjà chargées par la recherche principale, inutile de refaire l'appel parallèle
        if (filterHoraire && filterHoraire.listeValues?.length > 0 && filterTypeEntite && filterTypeEntite.listeValues?.length > 0) {
            filterHoraire.loading = false;
            filterTypeEntite.loading = false;
            return;
        }

        const params = new HttpParams().set('idAbattement', '0');
        this.http.post<Result<any>>(`${environment.baseUrl}/controller/IJ/consultAbattement`, null, { params })
            .pipe(
                finalize(() => {
                    if (filterHoraire) {
                        filterHoraire.loading = false;
                    }
                    if (filterTypeEntite) {
                        filterTypeEntite.loading = false;
                    }
                })
            )
            .subscribe(result => {
                if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    const listeHoraire = result?.data?.listeHoraire ?? [];
                    const listeTypeEntite = result?.data?.listeTypeEntite ?? [];

                    if (filterHoraire && !(filterHoraire.listeValues?.length > 0)) {
                        filterHoraire.listeValues = this.formatFilterOptions(listeHoraire, 'idHoraire');
                    }

                    if (filterTypeEntite && !(filterTypeEntite.listeValues?.length > 0)) {
                        filterTypeEntite.listeValues = this.formatFilterOptions(listeTypeEntite, 'idTypeEntite');
                    }
                }
            });
    }

    /**
     * Redirection vers l'écran de création d'un abattement (comportement V9)
     */
    private createAbattement(): void {
        void this.router.navigate(['Admin/Frais/Indemnites/Abattements/0']);
    }

    /**
     * Ouverture d'un abattement existant
     */
    private consultAbattement(abattement: IndemnitesAbattementItem): void {
        void this.router.navigate([`Admin/Frais/Indemnites/Abattements/${abattement.idAbattement}`]);
    }

    /**
     * Formate une liste d'options référentielles brute en valeurs de filtre exploitables (FilterValue[])
     */
    private formatFilterOptions(options: Array<any>, keyId: string): Array<any> {
        const mappedValues = (options ?? [])
            .filter(option => option?.[keyId] && option[keyId] !== 0)
            .map(option => ({
                code: String(option[keyId]),
                libelle: option.libelle
            }));

        return [
            { code: '0', libelle: this.translateService.instant('admin.frais.indemnites.abattements.detail.generalites.tous') },
            ...mappedValues
        ];
    }
}

/**
 * Modèle d'item de la liste des abattements
 */
export class IndemnitesAbattementItem implements ListItem {
    /** Identifiant de l'abattement */
    idAbattement: number;

    /** Identifiant de l'horaire */
    idHoraire: number;

    /** Identifiant du type entité */
    idTypeEntite: number;

    /** Libellé de la règle d'attribution */
    libelleHoraire: string;

    /** Libellé du type entité */
    libelleTypeEntite: string;

    /** Nombre de jours */
    nbJours: number;

    /** Pourcentage d'abattement */
    pourcentageAbattement: number;

    /**
     * Constructeur
     */
    constructor(data?: Partial<IndemnitesAbattementItem>) {
        Object.assign(this,data);
    }

    /**
     * Libellé de règle d'attribution à afficher (fallback "Tous" comme en V9)
     */
    getLibelleHoraireAffichage(): string {
        return this.libelleHoraire || 'Tous';
    }

    /**
     * Libellé de type entité à afficher (fallback "Tous" comme en V9)
     */
    getLibelleTypeEntiteAffichage(): string {
        return this.libelleTypeEntite || 'Tous';
    }

    /**
     * Retourne la clé de l'élément
     */
    getKey(): number {
        return this.idAbattement;
    }
}
